import { api } from './authService';

export interface HealthCheckLog {
  id: number;
  tenant_code: string;
  service_id: number;
  service_name: string;
  health_status: 'healthy' | 'unhealthy' | 'unknown';
  response_time?: number;
  error_message?: string;
  checked_at: string;
  created_at: string;
}

export interface HealthCheckLogQuery {
  tenant_code?: string;
  service_id?: number;
  health_status?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface HealthCheckLogResponse {
  success: boolean;
  data: HealthCheckLog[];
  total: number;
  page: number;
  limit: number;
}

export interface HealthCheckStats {
  total: number;
  healthy_count: number;
  unhealthy_count: number;
  unknown_count: number;
  avg_response_time: number;
}

export interface HealthCheckStatsResponse {
  success: boolean;
  data: HealthCheckStats;
}

export interface HealthCheckStatsQuery {
  tenant_code?: string;
  start_date?: string;
  end_date?: string;
}

export interface HealthCheckSchedulerStatus {
  running: boolean;
  lastCheckTime: string | null;
  schedule: string;
  cleanupSchedule: string;
  retentionDays?: number;
}

export interface HealthCheckSchedulerStatusResponse {
  success: boolean;
  data: HealthCheckSchedulerStatus;
}

export interface HealthCheckSchedulerToggleResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const healthCheckService = {
  /**
   * 获取健康检查日志
   */
  async getHealthCheckLogs(query: HealthCheckLogQuery = {}): Promise<HealthCheckLogResponse> {
    const params = new URLSearchParams();
    
    if (query.tenant_code) params.append('tenant_code', query.tenant_code);
    if (query.service_id) params.append('service_id', query.service_id.toString());
    if (query.health_status) params.append('health_status', query.health_status);
    if (query.start_date) params.append('start_date', query.start_date);
    if (query.end_date) params.append('end_date', query.end_date);
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());

    const response = await api.get<HealthCheckLogResponse>(
      `/health-check-logs?${params.toString()}`
    );
    return response.data;
  },

  /**
   * 获取健康检查统计信息
   */
  async getHealthCheckStats(query: HealthCheckStatsQuery = {}): Promise<HealthCheckStatsResponse> {
    const params = new URLSearchParams();
    
    if (query.tenant_code) params.append('tenant_code', query.tenant_code);
    if (query.start_date) params.append('start_date', query.start_date);
    if (query.end_date) params.append('end_date', query.end_date);

    const response = await api.get<HealthCheckStatsResponse>(
      `/health-check-logs/stats?${params.toString()}`
    );
    return response.data;
  },

  /**
   * 获取健康检查调度器状态
   */
  async getSchedulerStatus(): Promise<HealthCheckSchedulerStatusResponse> {
    const response = await api.get<HealthCheckSchedulerStatusResponse>('/health-check-scheduler/status');
    return response.data;
  },

  /**
   * 启动健康检查调度器
   */
  async startScheduler(): Promise<HealthCheckSchedulerToggleResponse> {
    const response = await api.post<HealthCheckSchedulerToggleResponse>('/health-check-scheduler/start');
    return response.data;
  },

  /**
   * 停止健康检查调度器
   */
  async stopScheduler(): Promise<HealthCheckSchedulerToggleResponse> {
    const response = await api.post<HealthCheckSchedulerToggleResponse>('/health-check-scheduler/stop');
    return response.data;
  },

  /**
   * 获取保留天数
   */
  async getRetentionDays(): Promise<{ success: boolean; data: { retentionDays: number } }> {
    const response = await api.get<{ success: boolean; data: { retentionDays: number } }>(
      '/health-check-scheduler/retention',
    );
    return response.data;
  },

  /**
   * 更新保留天数
   */
  async setRetentionDays(retentionDays: number): Promise<{ success: boolean; data: { retentionDays: number }; message?: string; error?: string }> {
    const response = await api.put<{ success: boolean; data: { retentionDays: number }; message?: string; error?: string }>(
      '/health-check-scheduler/retention',
      { retentionDays },
    );
    return response.data;
  },

  /**
   * 立即清理超过保留期的旧日志
   */
  async cleanupNow(): Promise<{ success: boolean; data: { healthCheckLogs: number; emailLogs: number; retentionDays: number; cutoffDate: string }; message?: string; error?: string }> {
    const response = await api.post<{ success: boolean; data: { healthCheckLogs: number; emailLogs: number; retentionDays: number; cutoffDate: string }; message?: string; error?: string }>(
      '/health-check-scheduler/cleanup',
    );
    return response.data;
  },
};

